import z from 'zod';

export const messageLinkSchema = z
  .object({
    messageLink: z
      .string()
      .url()
      .describe(
        'The link to an alert message for which the playbook should run'
      ),
  })
  .strict();
export const alertDetailsSchema = z
  .object({
    title: z.string().describe('The alert title'),
    time: z.string().datetime().describe('The alert time').optional(),
    attributes: z
      .record(z.string())
      .describe('The alert attributes')
      .optional(),
    details: z.string().describe('The alert details').optional(),
  })
  .strict();

const bookYamlStrSchema = z.string().describe('Local playbook content');

const verboseSchema = z
  .boolean()
  .describe(
    "Include the Moose's reasoning (thought processes) at every step of the investigation — for each action, highlights, each orchestration decision (in case of an orchestrated run), and the final conclusion. Defaults to false; set to true when debugging the Moose's reasoning."
  );

const actionTitleSchema = z
  .string()
  .describe(
    'The action title to run the playbook with (eg. CPU Utilization is too high - avg idle time below 20%)'
  );

export const bookPathSchema = z
  .string()
  .describe(
    'A path to a local YAML playbook file to run. If not provided, playbook defaults to the one mapped to the alert in the Wild Moose app.'
  );

export const bookNameSchema = z.string().describe('The name of the playbook');

const testPathSchema = z
  .string()
  .describe('The YAML file path with bookName and all the expectations');

const baseTestActionSchema = z.object({
  bookPath: bookPathSchema
    .optional()
    .describe(bookPathSchema.description ?? ''),
  actionTitle: actionTitleSchema.optional(),
  outputFile: z
    .string()
    .optional()
    .describe('The file path to save the output'),
});

const monitorsSchema = z.array(
  z.object({
    monitorName: z.string().describe(
      `Monitor name format: The system uses {{...}} placeholders for variable components.

      CONVERSION GUIDELINES:
      - Analyze the user's input to determine what represents variable data versus literal text
      - Evaluate each component individually - convert variable patterns and regex/placeholder syntax to {{...}} format with meaningful names, while keeping all other text unchanged
      - Each placeholder name must be unique - a named group cannot appear more than once
      - If no meaningful name is available, use sequential numbering: {{placeholder1}}, {{placeholder2}}, etc.
      - Each variable component must be converted to its own separate placeholder - do not combine multiple variables into a single placeholder, even when they appear adjacent to each other.
      - If the user references a monitor name from a file or existing source, first extract and present the exact monitor name to the user for validation. Only after the user confirms it is correct, proceed to apply the conversion guidelines.


      If you are uncertain about how to interpret the input, ask the user for clarification before creating the parameters for the call.`
    ),
    monitorBody: z.string().optional(),
  })
);
export const channelIdSchema = z
  .string()
  .describe(
    "should pass one channel Identifier: channelId or channelName. The channel must not start with '#'."
  );
const channelNameSchema = z
  .string()
  .describe(
    "should pass one channel Identifier: channelId or channelName. The channel must not start with '#'."
  );
const dateTimeSchema = z
  .string()
  .datetime()
  .describe('ISO 8601 datetime in UTC');
const paginationLimitSchema = z
  .number()
  .describe('The number of alerts to return');

const baseActionResultSchema = z.object({
  title: z.string().nonempty(),
  shortAnswer: z.string().optional(),
  insights: z.string().optional(),
  response: z.object({ summary: z.string() }),
  rawResponse: z.unknown(),
});

const actionResultSchema = baseActionResultSchema.extend({
  mostImportantLink: z.string().optional(),
  structuredOutputs: z.array(z.string()).optional(),
  actionKey: z.string().nonempty().optional(),
});

const runPlaybookOutput = z.union([
  z.object({ error: z.string() }),
  z
    .object({
      highlights: z.string(),
      gadgetsHighlights: z.array(
        z.object({
          title: z.string(),
          args: z.record(z.unknown()).optional(),
          highlights: z.string(),
        })
      ),
      enrichmentLink: z.string().optional(),
      enrichmentKey: z.string().optional(),
      actionResults: z.array(actionResultSchema),
    })
    .passthrough(),
]);

const testActionResultSchema = z.union([
  z.object({
    alertLink: z.string().optional(),
    alertDetails: alertDetailsSchema.optional(),
    expected: z.string(),
    actual: baseActionResultSchema,
  }),
  z.object({
    alertLink: z.string().optional(),
    alertDetails: alertDetailsSchema.optional(),
    expected: z.string(),
    error: z.string(),
  }),
]);

export const comparisonResultSchema = z.object({
  highlightsComparison: z
    .object({
      overallSummary: z.string(),
      sections: z.record(z.unknown()),
      isSimilar: z.boolean(),
    })
    .passthrough(),
  prodResult: z
    .object({
      highlights: z.string(),
      enrichmentLink: z.string(),
      actionableMitigations: z.array(
        z.object({ toolName: z.string() }).passthrough()
      ),
    })
    .passthrough(),
  currentResult: z
    .object({
      highlights: z.string().optional(),
      actionableMitigations: z.array(
        z.object({ toolName: z.string() }).passthrough()
      ),
    })
    .passthrough(),
  mitigationsComparison: z.record(z.unknown()),
});

const getAlertOutputSchema = z
  .object({
    alert: z
      .object({
        channelId: z.string().optional(),
        title: z.string().optional(),
        rawTitle: z.string().optional(),
        details: z.string().optional(),
        time: z.string().optional(),
        attributes: z.record(z.unknown()).optional(),
        monitorId: z.union([z.string(), z.number()]).optional(),
        isSupported: z.boolean().optional(),
        link: z.string().optional(),
      })
      .passthrough(),
    book: z.string().nullable(),
    variables: z.record(z.unknown()),
  })
  .passthrough();

export default {
  api: {
    runPlaybookInput: z.union([
      messageLinkSchema.extend({
        bookName: bookNameSchema.optional(),
        bookYamlStr: bookYamlStrSchema.optional(),
        verbose: verboseSchema.optional(),
      }),
      alertDetailsSchema.extend({
        bookName: bookNameSchema.optional(),
        bookYamlStr: bookYamlStrSchema.optional(),
        verbose: verboseSchema.optional(),
      }),
    ]),
    runPlaybookOutput,
    runPlaybookBatchInput: z.object({
      inputList: z.array(z.union([messageLinkSchema, alertDetailsSchema])),
      bookYamlStr: bookYamlStrSchema.optional(),
      bookName: bookNameSchema.optional(),
      verbose: verboseSchema.optional(),
    }),
    runPlaybookBatchOutput: z.array(runPlaybookOutput),
    testActionInput: baseTestActionSchema.extend({
      inputList: z.array(
        z.union([
          messageLinkSchema.extend({ expectation: z.string() }),
          alertDetailsSchema.extend({ expectation: z.string() }),
        ])
      ),
    }),
    testActionOutput: z.array(testActionResultSchema),
    testActionResultSchema,
    getFiredAlertsInput: z.object({
      monitors: monitorsSchema,
      channelId: channelIdSchema.optional(),
      channelName: channelNameSchema.optional(),
      from: dateTimeSchema.optional(),
      to: dateTimeSchema.optional(),
      limit: paginationLimitSchema.optional(),
    }),
    getFiredAlertsOutput: z.array(
      z
        .object({
          title: z.string().nonempty(),
          rawTitle: z.string().nonempty().optional(),
          details: z.string().nonempty().optional(),
          time: z.string().nonempty(),
          id: z.string().nonempty().optional(),
          monitorId: z.number().optional(),
          attributes: z.record(z.unknown()).optional(),
          channelId: z.string().nonempty(),
          link: z.string().nonempty().optional(),
        })
        .passthrough()
    ),
    fetchTeleportContextInput: z
      .object({
        enrichmentLink: z.string().url().optional(),
        alertLink: z.string().url().optional(),
      })
      .refine(
        ({ enrichmentLink, alertLink }) =>
          [enrichmentLink, alertLink].filter((link) => link).length === 1,
        'Exactly one of enrichmentLink or alertLink is required'
      ),
    fetchTeleportContextOutput: z.unknown(),
    getAlertInput: messageLinkSchema,
    getAlertOutput: getAlertOutputSchema,
    getPlaybookInput: z.object({ name: bookNameSchema }),
    getPlaybookOutput: z.unknown(),
    postFindingsToEnrichmentThreadInput: z.object({
      enrichmentLink: z.string().url(),
      summary: z.string().min(10),
      toolName: z.string().min(1),
    }),
    postFindingsToEnrichmentThreadOutput: z
      .object({
        ok: z.boolean(),
        ts: z.string(),
        permalink: z.string().nullish(),
      })
      .passthrough(),
    reliabilityCenterAccessOutput: z.object({ enabled: z.boolean() }),
    getTuningCasesInput: z.object({
      query: z
        .object({
          from: dateTimeSchema.optional(),
          to: dateTimeSchema.optional(),
          limit: z.number().int().positive().optional(),
          monitors: z.array(
            z.object({
              monitorName: z.string(),
            })
          ),
        })
        .optional(),
    }),
    getTuningCasesOutput: z.array(
      z
        .object({
          caseRef: z.string(),
          title: z.string().nullable(),
          firedAt: z.string().nullable(),
          alertLink: z.string().nullable(),
          truth: z.object({
            at: z.string(),
            expectation: z.string(),
            source: z.string().optional(),
            ref: z.string().optional(),
          }),
        })
        .passthrough()
    ),
  },
  tool: {
    runPlaybookInput: z.union([
      messageLinkSchema.extend({ bookPath: bookPathSchema.optional() }),
      alertDetailsSchema.extend({ bookPath: bookPathSchema.optional() }),
    ]),
    runPlaybookBatchInput: z.object({
      inputList: z.array(z.union([messageLinkSchema, alertDetailsSchema])),
      bookPath: bookPathSchema.optional(),
      bookName: bookNameSchema.optional(),
      verbose: verboseSchema.optional(),
      outputPath: z
        .string()
        .optional()
        .describe(
          'File path to write the output to. When provided, results are saved to this file instead of being returned inline, preventing large responses from filling up context.'
        ),
    }),
    testActionCommandInput: baseTestActionSchema.extend({
      testPath: testPathSchema,
    }),

    testActionInput: baseTestActionSchema.extend({
      inputList: z.array(
        z.union([
          messageLinkSchema.extend({ expectation: z.string() }),
          alertDetailsSchema.extend({ expectation: z.string() }),
        ])
      ),
      outputFile: z
        .string()
        .optional()
        .describe('The file path to save the output'),
    }),
    getFiredAlertsInput: z.object({
      monitors: monitorsSchema,
      channelId: channelIdSchema.optional(),
      channelName: channelNameSchema.optional(),
      from: z
        .string()
        .datetime()
        .describe('ISO 8601 datetime in UTC')
        .optional(),
      to: z
        .string()
        .datetime()
        .describe(
          "ISO 8601 datetime in UTC, and should be later than 'from' datetime"
        )
        .optional(),
      limit: paginationLimitSchema.optional(),
    }),
    fetchTeleportContextInput: z
      .object({
        enrichmentLink: z
          .string()
          .url()
          .optional()
          .describe(
            'Link to the Slack message of the enrichment to fetch. Prefer this over alertLink: it names the enrichment directly, and it is the only way to reach one posted to a dedicated enrichment channel rather than the alert channel. In a handoff session, use the enrichment link from the handoff context as-is.'
          ),
        alertLink: z
          .string()
          .url()
          .optional()
          .describe(
            'Link to the Slack message of the alert the enrichment was produced for. Use this only when no enrichment link is available. In a handoff session, use the alert link from the handoff context as-is.'
          ),
      })
      .strict()
      .refine(
        ({ enrichmentLink, alertLink }) =>
          [enrichmentLink, alertLink].filter((link) => link).length === 1,
        'Exactly one of enrichmentLink or alertLink is required'
      ),
    getAlertInput: z
      .object({
        messageLink: z
          .string()
          .url()
          .describe('The link to a Slack or Teams alert message to parse'),
      })
      .strict(),
    getPlaybookInput: z.object({
      name: bookNameSchema,
    }),
    postFindingsToEnrichmentThreadInput: z
      .object({
        enrichmentLink: z
          .string()
          .url()
          .describe(
            'The link to the enrichment message to post the findings to. Use the enrichmentLink returned by the fetch-teleport-context tool as-is — do not construct or edit it.'
          ),
        summary: z
          .string()
          .min(10)
          .describe(
            'The investigation findings to post: what was investigated, the root cause (or the current best hypothesis), the key supporting evidence, and the recommended next action. Posted verbatim as the reply body, so keep it concise and skimmable.'
          ),
        toolName: z
          .string()
          .min(1)
          .describe(
            "The name of the AI assistant tooling this MCP server is running in (e.g. 'Claude Code', 'Cursor'), used to attribute the posted findings."
          ),
      })
      .strict(),
    getTuningCasesInput: z
      .object({
        from: z
          .string()
          .datetime()
          .optional()
          .describe(
            'Start of the time window for scanning alert channels (ISO 8601 UTC). Defaults to 30 days ago.'
          ),
        to: z
          .string()
          .datetime()
          .optional()
          .describe(
            'End of the time window for scanning alert channels (ISO 8601 UTC). Defaults to now.'
          ),
        limit: z
          .number()
          .int()
          .positive()
          .optional()
          .describe('Maximum number of cases to return. Defaults to 100.'),
        monitors: z.array(
          z.object({
            monitorName: z
              .string()
              .describe(
                'Copy the monitorName exactly as they appear in the playbook '
              ),
          })
        ),
      })
      .strict(),
  },
};
